import { el, toggle } from './dom';
import { GROUP_OF, groupLegend, legendKey, TOGGLEABLE, type LegendGroup, type LegendItem } from './mapLegend';
import { applyIcon, iconFor } from './mapIcons';
import { MapCamera, type PinchStart, type TapResult, type WorldBounds } from './mapView';
import { distanceText } from './ownCar';
import type { NavAction, Screen, Snd } from './screens';

/**
 * The full map screen (M / Tab, the MAP knob, a tap on the minimap, the book's MAP page): the whole screen,
 * north up, zoom and pan (pinch / drag / double-tap on touch, wheel / drag with a mouse, + / − / ME buttons,
 * arrow keys), a key that is always on show on a desktop and a drawer behind a big KEY button on a phone. The
 * key is grouped (You and your car · Jobs and contacts · Shops and garages · Police · Online players) and the
 * last four groups have a switch that takes them off both maps. **Find my car** (top right of the map) glides
 * to your car, rings it and offers Route here. A tap on a place asks first — a small card with **Route here**
 * (the route arrows lead there) — and a tap on your flag offers **Clear route**; nothing changes until a button
 * says so. It closes only from its ✕ button, Esc / M / Tab, or the pad's B — taps belong to the map. The
 * drawing is Minimap.drawFull, asked for by uiSystem when `wantsDraw` says something changed.
 */

export interface FullMapHost {
  close(): void;
  sound(s: Snd): void;
  /** Where you are now (world). */
  player(): { x: number; z: number };
  /** What a tap at (sx, sy) CSS px in the map means: a place to route to, your flag (clear), or nothing. */
  pick(sx: number, sy: number, cam: MapCamera): TapResult | null;
  /** Route here: the place becomes your mark (the arrows lead there). */
  route(p: { x: number; z: number; label: string }): void;
  /** Clear route: rub out your mark. */
  clearRoute(): void;
  /** Your mark's name, for the card. */
  markLabel(): string | null;
  /** Nearest marker of a key line to (x, z), for "show me where" (null: none on the map). */
  locate(item: LegendItem, x: number, z: number): { x: number; z: number } | null;
  /** Your mark, if any (changes the hint line). */
  hasMark(): boolean;
  /** Your car to find (the motor, else the last car you drove; ownCar.ts), or null: none; `inIt`: you drive it. */
  car(): { x: number; z: number; label: string; glyph?: string; inIt: boolean } | null;
  /** The key's switches: which groups are off the map now, and a change to that. */
  hidden(): ReadonlySet<LegendGroup>;
  setHidden(groups: ReadonlySet<LegendGroup>): void;
}

/** The key opens on the first look at the map each session (on a phone) and stays folded after that. */
let mapKeySeen = false;

/** Fingers moving less than this (CSS px) between down and up is a tap, not a drag. */
const TAP_SLOP = 9;
/** Two taps within this long (ms) and this close (CSS px) zoom in on the spot. */
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_PX = 28;
/** Redraws a second while nothing is touched (police dots move, the objective pulses). */
const IDLE_FPS = 15;

export class FullMapScreen implements Screen {
  readonly root: HTMLElement;
  readonly canvas: HTMLCanvasElement;
  readonly cam: MapCamera;
  /** Canvas buffer px per CSS px. */
  dpr = 1;
  /** The key line just tapped and how long ago (s): its markers flash. */
  flash: { kind: LegendItem['kind']; glyph?: string; age: number } | null = null;
  private readonly pane: HTMLElement;
  private readonly key: HTMLElement;
  private readonly keyList: HTMLElement;
  private readonly keyBtn: HTMLButtonElement | null = null;
  private readonly findBtn: HTMLButtonElement;
  private readonly findText: HTMLElement;
  private readonly scale: HTMLElement;
  private readonly scaleBar: HTMLElement;
  private readonly scaleText: HTMLElement;
  private scaleZoom = 0;
  private readonly hint: HTMLElement;
  private readonly card: HTMLElement;
  private readonly cardTitle: HTMLElement;
  private readonly cardGo: HTMLButtonElement;
  /** A place tapped and not yet routed to (drawn as a dashed ring), or your flag tapped ('clear'). */
  pending: { x: number; z: number; label: string } | null = null;
  /** The boxes of the DOM over the map (the Find my car pill, the zoom knobs, the compass, the scale bar; CSS px in
   *  the pane), so no caption lands under them. */
  readonly reserved: { x0: number; y0: number; x1: number; y1: number }[] = [];
  private readonly zoomBox: HTMLElement;
  private cardMode: 'route' | 'clear' | null = null;
  private legendSig = '';
  /** The group switches on show, by group (a tap on a line of a switched-off group turns it back on). */
  private readonly switches = new Map<LegendGroup, { sw: HTMLButtonElement; sec: HTMLElement }>();
  private dirty = true;
  private idle = 0;
  private findTimer = 0;
  private findSig = '';
  private hadMark: boolean | null = null;
  private readonly ptrs = new Map<number, { x: number; y: number }>();
  private rect: DOMRect | null = null;
  private tapStart: { id: number; x: number; y: number; t: number } | null = null;
  private lastTap: { x: number; y: number; t: number } | null = null;
  private readonly pinch: PinchStart = { wx: 0, wz: 0, zoom: 1, dist: 1 };
  private ro: ResizeObserver | null = null;
  private readonly onResize = (): void => this.measure();
  private readonly onKey = (e: KeyboardEvent): void => {
    if ((e.target as Element | null)?.closest?.('[data-bh-text-entry]')) return;
    if (e.code === 'Equal' || e.code === 'NumpadAdd') this.zoomStep(1.5);
    else if (e.code === 'Minus' || e.code === 'NumpadSubtract') this.zoomStep(1 / 1.5);
    else if (e.code === 'KeyC') this.centreOnMe();
    else if (e.code === 'KeyV') this.findCar();
    else return;
    e.preventDefault();
  };

  constructor(
    parent: HTMLElement,
    private readonly host: FullMapHost,
    district: string,
    legend: readonly LegendItem[],
    private readonly touch: boolean,
    bounds: WorldBounds,
  ) {
    this.cam = new MapCamera(bounds);
    this.root = el('div', 'bh-fullmap', undefined, parent);
    const sheet = el('div', 'sheet bh-paper no-tape', undefined, this.root);
    const cap = el('div', 'cap', undefined, sheet);
    el('h2', '', 'The Borough', cap);
    el('span', 'where', district ? `You are in: ${district}` : '', cap);
    if (touch) {
      const btn = el('button', 'keybtn', 'Key', cap);
      btn.type = 'button';
      btn.setAttribute('aria-controls', 'bh-map-key');
      btn.addEventListener('click', () => {
        host.sound('click');
        this.toggleKey();
      });
      this.keyBtn = btn;
    }
    const close = el('button', 'close', undefined, cap);
    close.type = 'button';
    el('b', 'x', '✕', close);
    el('span', 'lbl', 'Back to the street', close);
    close.setAttribute('aria-label', 'Close the map — back to the street');
    close.addEventListener('click', () => host.close());

    const body = el('div', 'body', undefined, sheet);
    this.pane = el('div', 'pane', undefined, body);
    this.canvas = el('canvas', '', undefined, this.pane);
    this.canvas.setAttribute('aria-label', 'Map of the borough');
    // The vignette over the map's edges, the compass (the map is always north up) and the scale bar.
    el('div', 'vig', undefined, this.pane);
    const compass = el('div', 'compass bh-glass', undefined, this.pane);
    compass.setAttribute('aria-label', 'North is up');
    compass.insertAdjacentHTML('afterbegin', COMPASS_SVG);
    this.scale = el('div', 'scale bh-glass', undefined, this.pane);
    this.scaleBar = el('i', '', undefined, this.scale);
    this.scaleText = el('span', '', '', this.scale);
    const zoom = el('div', 'zoom', undefined, this.pane);
    this.zoomBox = zoom;
    this.button(zoom, 'zin', '+', 'Zoom in', () => this.zoomStep(1.6));
    this.button(zoom, 'zout', '−', 'Zoom out', () => this.zoomStep(1 / 1.6));
    this.button(zoom, 'me', 'Me', 'Centre on me', () => this.centreOnMe());
    // Find my car: a pill top right with the car and how far it is; hidden while you have no car.
    this.findBtn = el('button', 'findcar bh-glass', undefined, this.pane);
    this.findBtn.type = 'button';
    this.findBtn.hidden = true;
    this.findBtn.insertAdjacentHTML('afterbegin', CAR_SVG);
    this.findText = el('span', 'txt', 'Find my car', this.findBtn);
    this.findBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.host.sound('tick');
      this.findCar();
    });
    this.hint = el('div', 'hint', '', this.pane);
    // The tap card: what you tapped, and the one thing to do with it.
    this.card = el('div', 'route-card bh-soot', undefined, this.pane);
    this.cardTitle = el('span', 'what', '', this.card);
    this.cardGo = el('button', 'go', 'Route here', this.card);
    this.cardGo.type = 'button';
    this.cardGo.addEventListener('click', (e) => {
      e.stopPropagation();
      this.confirmCard();
    });
    const no = el('button', 'no', '✕', this.card);
    no.type = 'button';
    no.setAttribute('aria-label', 'Never mind');
    no.addEventListener('click', (e) => {
      e.stopPropagation();
      this.host.sound('tick');
      this.closeCard();
    });

    this.key = el('div', 'legend bh-glass', undefined, body);
    this.key.id = 'bh-map-key';
    el('b', 'lh', 'Key', this.key);
    el('small', 'kh', touch ? 'Tap a line to find it · a switch takes that group off the map' : 'Click a line to find it · a switch takes that group off the map', this.key);
    this.keyList = el('div', 'groups', undefined, this.key);
    this.setLegend(legend);
    if (touch) this.setKey(!mapKeySeen);
    mapKeySeen = true;

    this.installInput();
    this.measure();
    const p = host.player();
    this.cam.openOn(p.x, p.z);
    this.refreshHint();
    this.refreshFind();
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(this.onResize);
      this.ro.observe(this.pane);
    } else window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKey);
  }

  private button(parent: HTMLElement, cls: string, text: string, label: string, run: () => void): HTMLButtonElement {
    const b = el('button', cls, text, parent);
    b.type = 'button';
    b.setAttribute('aria-label', label);
    b.title = label;
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      this.host.sound('tick');
      run();
    });
    return b;
  }

  // ------------------------------------------------------------------ key

  /** What's on the map changed: rebuild the key's lines (only when they differ), under their group headings. */
  setLegend(items: readonly LegendItem[]): void {
    const sig = legendKey(items);
    if (sig === this.legendSig) return;
    this.legendSig = sig;
    this.keyList.textContent = '';
    this.switches.clear();
    const hidden = this.host.hidden();
    for (const grp of groupLegend(items)) {
      const sec = el('section', `grp grp-${grp.group}`, undefined, this.keyList);
      const head = el('div', 'gh', undefined, sec);
      el('b', '', grp.label, head);
      if (TOGGLEABLE.includes(grp.group)) {
        const off = hidden.has(grp.group);
        const sw = el('button', 'switch', undefined, head);
        sw.type = 'button';
        sw.setAttribute('role', 'switch');
        sw.setAttribute('aria-checked', off ? 'false' : 'true');
        sw.setAttribute('aria-label', `${grp.label} on the map`);
        el('i', 'knob', undefined, sw);
        el('span', 'st', off ? 'Off' : 'On', sw);
        toggle(sw, 'off', off);
        toggle(sec, 'is-off', off);
        sw.addEventListener('click', () => this.toggleGroup(grp.group, sw, sec));
        this.switches.set(grp.group, { sw, sec });
      }
      const ul = el('ul', '', undefined, sec);
      for (const it of grp.items) {
        const li = el('li', '', undefined, ul);
        const b = el('button', '', undefined, li);
        b.type = 'button';
        const sw = el('i', `sw sw-${it.kind}${it.kind === 'car' && it.glyph === '★' ? ' sw-motor' : ''}`, it.kind === 'car' ? '' : (it.glyph ?? ''), b);
        if (it.kind === 'car') sw.insertAdjacentHTML('afterbegin', CAR_SVG);
        const icon = iconFor(it.kind, it.glyph);
        if (icon) applyIcon(sw, icon);
        el('span', '', it.label, b);
        b.addEventListener('click', () => this.showItem(it));
      }
    }
  }

  /** A switch on a group heading: that group goes off (or back on) both maps; remembered across sessions. */
  private toggleGroup(group: LegendGroup, sw: HTMLButtonElement, sec: HTMLElement): void {
    this.host.sound('click');
    const next = new Set(this.host.hidden());
    const off = !next.has(group);
    if (off) next.add(group);
    else next.delete(group);
    this.host.setHidden(next);
    sw.setAttribute('aria-checked', off ? 'false' : 'true');
    toggle(sw, 'off', off);
    toggle(sec, 'is-off', off);
    const st = sw.querySelector('.st');
    if (st) st.textContent = off ? 'Off' : 'On';
    this.dirty = true;
  }

  /** A tap on a key line: flash those markers; glide to the nearest one when none is on screen. */
  private showItem(it: LegendItem): void {
    this.host.sound('tick');
    // A line of a group that is switched off: the group comes back on, else there is nothing to see.
    const grp = GROUP_OF[it.kind];
    const s = this.switches.get(grp);
    if (s && this.host.hidden().has(grp)) this.toggleGroup(grp, s.sw, s.sec);
    this.flash = it.glyph ? { kind: it.kind, glyph: it.glyph, age: 0 } : { kind: it.kind, age: 0 };
    const c = this.cam;
    const at = this.host.locate(it, c.cx, c.cz);
    if (at) {
      const sx = c.toScreenX(at.x);
      const sy = c.toScreenY(at.z);
      const m = 30;
      if (sx < m || sx > c.w - m || sy < m || sy > c.h - m) c.glideTo(at.x, at.z);
    }
    // On a phone the drawer covers part of the map: fold it so the flash is seen.
    if (this.touch) this.setKey(false);
    this.dirty = true;
  }

  private setKey(open: boolean): void {
    toggle(this.root, 'key-open', open);
    if (this.keyBtn) {
      toggle(this.keyBtn, 'on', open);
      this.keyBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  toggleKey(): void {
    this.setKey(!this.keyOpen);
  }

  /** The key is on show (always on a desktop; the drawer on a phone). */
  get keyOpen(): boolean {
    return !this.touch || this.root.classList.contains('key-open');
  }

  private refreshHint(): void {
    const has = this.host.hasMark();
    if (has === this.hadMark) return;
    this.hadMark = has;
    const verb = this.touch ? 'Tap' : 'Click';
    this.hint.textContent = has
      ? `${verb} your flag to clear the route · ${verb} another place to route there instead`
      : `${verb} a place, then Route here — arrows on the road lead you there`;
  }

  // ------------------------------------------------------------------ your car

  /** The Find my car pill: the car's distance from you, a few times a second; hidden while you have none. */
  private refreshFind(): void {
    const car = this.host.car();
    if (!car) {
      if (!this.findBtn.hidden) {
        this.findBtn.hidden = true;
        this.measureFind();
      }
      return;
    }
    const p = this.host.player();
    const text = car.inIt ? 'You’re in your car' : `Find my car · ${distanceText(Math.hypot(car.x - p.x, car.z - p.z))}`;
    const sig = `${text}|${car.glyph ?? ''}`;
    if (sig === this.findSig) return;
    this.findSig = sig;
    this.findText.textContent = text;
    this.findBtn.title = car.label;
    this.findBtn.setAttribute('aria-label', car.inIt ? 'You are in your car' : `${car.label}: find it on the map`);
    toggle(this.findBtn, 'motor', car.glyph === '★');
    this.findBtn.hidden = false;
    this.measureFind();
  }

  /** Where the DOM lies over the map (a little padding), for the captions to keep clear of. */
  private measureFind(): void {
    const pane = this.pane.getBoundingClientRect();
    this.reserved.length = 0;
    const add = (e: HTMLElement | null, pad: number): void => {
      if (!e || e.hidden) return;
      const r = e.getBoundingClientRect();
      if (r.width === 0) return;
      this.reserved.push({ x0: r.left - pane.left - pad, y0: r.top - pane.top - pad, x1: r.right - pane.left + pad, y1: r.bottom - pane.top + pad });
    };
    add(this.findBtn, 10);
    add(this.zoomBox, 6);
    add(this.scale, 4);
    add(this.pane.querySelector<HTMLElement>('.compass'), 4);
  }

  /** Find my car: glide to it, ring it, and offer Route here (or centre on you when you're sitting in it). */
  findCar(): void {
    const car = this.host.car();
    if (!car) return;
    if (car.inIt) {
      this.centreOnMe();
      return;
    }
    this.cam.glideTo(car.x, car.z, Math.max(this.cam.zoom, this.cam.minZoom * 2.4));
    this.flash = car.glyph ? { kind: 'car', glyph: car.glyph, age: 0 } : { kind: 'car', age: 0 };
    this.pending = { x: car.x, z: car.z, label: car.label };
    this.cardMode = 'route';
    this.cardTitle.textContent = car.label;
    this.cardGo.textContent = 'Route here';
    toggle(this.cardGo, 'clear', false);
    toggle(this.root, 'card-open', true);
    if (this.touch) this.setKey(false);
    this.dirty = true;
  }

  // ------------------------------------------------------------------ the tap card

  /** A tap on the map: ask what to do with it (nothing changes until a button is pressed). */
  private onTap(sx: number, sy: number): void {
    const r = this.host.pick(sx, sy, this.cam);
    if (!r) {
      this.closeCard();
      return;
    }
    this.host.sound('tick');
    if (r.kind === 'clear') {
      this.pending = null;
      this.cardMode = 'clear';
      this.cardTitle.textContent = `Your route: ${this.host.markLabel() ?? 'your mark'}`;
      this.cardGo.textContent = 'Clear route';
    } else {
      this.pending = { x: r.x, z: r.z, label: r.label };
      this.cardMode = 'route';
      this.cardTitle.textContent = r.label;
      this.cardGo.textContent = 'Route here';
    }
    toggle(this.cardGo, 'clear', this.cardMode === 'clear');
    toggle(this.root, 'card-open', true);
    this.dirty = true;
  }

  private confirmCard(): void {
    if (this.cardMode === 'clear') this.host.clearRoute();
    else if (this.cardMode === 'route' && this.pending) this.host.route(this.pending);
    this.closeCard();
  }

  private closeCard(): void {
    this.pending = null;
    this.cardMode = null;
    toggle(this.root, 'card-open', false);
    this.dirty = true;
  }

  /** The tap card is up (tests). */
  get cardOpen(): 'route' | 'clear' | null {
    return this.cardMode;
  }

  // ------------------------------------------------------------------ view

  private measure(): void {
    const r = this.pane.getBoundingClientRect();
    const w = Math.max(1, Math.round(r.width));
    const h = Math.max(1, Math.round(r.height));
    this.dpr = Math.min(2, typeof devicePixelRatio === 'number' && devicePixelRatio > 0 ? devicePixelRatio : 1);
    const bw = Math.round(w * this.dpr);
    const bh = Math.round(h * this.dpr);
    if (this.canvas.width !== bw) this.canvas.width = bw;
    if (this.canvas.height !== bh) this.canvas.height = bh;
    this.cam.resize(w, h);
    this.measureFind();
    this.dirty = true;
  }

  zoomStep(f: number): void {
    this.cam.glideZoom(f);
    this.dirty = true;
  }

  centreOnMe(): void {
    const p = this.host.player();
    this.cam.glideTo(p.x, p.z, Math.max(this.cam.zoom, this.cam.minZoom * 2.4));
    this.flash = { kind: 'you', age: 0 };
    this.dirty = true;
  }

  /** The view or the marks changed: draw on the next frame. */
  invalidate(): void {
    this.dirty = true;
  }

  /** The scale bar: a round number of metres that is 60–140 px wide at this zoom. */
  private refreshScale(): void {
    const z = this.cam.zoom;
    if (Math.abs(z - this.scaleZoom) < z * 0.01) return;
    this.scaleZoom = z;
    let m = 50;
    for (const c of SCALE_STEPS) {
      if (c * z >= 60) {
        m = c;
        break;
      }
      m = c;
    }
    this.scaleBar.style.width = `${Math.round(m * z)}px`;
    this.scaleText.textContent = m >= 1000 ? `${m / 1000} km` : `${m} m`;
  }

  /** Per frame: advance glides and the flash; true when the map should be redrawn now. */
  wantsDraw(dt: number): boolean {
    if (this.cam.step(dt)) this.dirty = true;
    this.refreshScale();
    if (this.flash) {
      this.flash.age += dt;
      if (this.flash.age > 2.5) this.flash = null;
      this.dirty = true;
    }
    this.refreshHint();
    this.findTimer -= dt;
    if (this.findTimer <= 0) {
      this.findTimer = 0.4;
      this.refreshFind();
    }
    this.idle += dt;
    if (this.idle >= 1 / IDLE_FPS) this.dirty = true;
    if (!this.dirty) return false;
    this.dirty = false;
    this.idle = 0;
    return true;
  }

  // ------------------------------------------------------------------ input

  private local(e: PointerEvent | WheelEvent): { x: number; y: number } {
    const r = this.rect ?? this.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private installInput(): void {
    const c = this.canvas;
    c.addEventListener('pointerdown', (e) => {
      this.rect = c.getBoundingClientRect();
      try {
        c.setPointerCapture(e.pointerId);
      } catch {
        /* synthetic events */
      }
      const p = this.local(e);
      this.ptrs.set(e.pointerId, p);
      if (this.ptrs.size === 1) this.tapStart = { id: e.pointerId, x: p.x, y: p.y, t: performance.now() };
      else {
        this.tapStart = null;
        this.startPinch();
      }
      e.preventDefault();
    });
    c.addEventListener('pointermove', (e) => {
      const prev = this.ptrs.get(e.pointerId);
      if (!prev) return;
      const p = this.local(e);
      if (this.ptrs.size >= 2) {
        prev.x = p.x;
        prev.y = p.y;
        const [a, b] = this.firstTwo();
        this.cam.pinch(this.pinch, (a.x + b.x) / 2, (a.y + b.y) / 2, Math.hypot(a.x - b.x, a.y - b.y));
      } else {
        const t = this.tapStart;
        if (t && Math.hypot(p.x - t.x, p.y - t.y) > TAP_SLOP) this.tapStart = null;
        if (!this.tapStart) this.cam.panBy(p.x - prev.x, p.y - prev.y);
        prev.x = p.x;
        prev.y = p.y;
      }
      this.dirty = true;
    });
    const up = (e: PointerEvent): void => {
      if (!this.ptrs.has(e.pointerId)) return;
      this.ptrs.delete(e.pointerId);
      const t = this.tapStart;
      if (e.type === 'pointerup' && t && t.id === e.pointerId && performance.now() - t.t < 600) {
        const p = this.local(e);
        const now = performance.now();
        const last = this.lastTap;
        if (last && now - last.t < DOUBLE_TAP_MS && Math.hypot(p.x - last.x, p.y - last.y) < DOUBLE_TAP_PX) {
          // A double tap: zoom in on the spot (the first tap's card goes away).
          this.lastTap = null;
          this.closeCard();
          this.cam.zoomAt(2, p.x, p.y);
          this.host.sound('tick');
        } else {
          this.lastTap = { x: p.x, y: p.y, t: now };
          this.onTap(p.x, p.y);
        }
      }
      this.tapStart = null;
      // From two fingers back to one: carry on as a drag from where that finger is.
      if (this.ptrs.size >= 2) this.startPinch();
    };
    c.addEventListener('pointerup', up);
    c.addEventListener('pointercancel', up);
    c.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        this.rect = c.getBoundingClientRect();
        const p = this.local(e);
        const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
        this.cam.zoomAt(Math.exp(-dy * 0.0018), p.x, p.y);
        this.dirty = true;
      },
      { passive: false },
    );
  }

  private firstTwo(): [{ x: number; y: number }, { x: number; y: number }] {
    const it = this.ptrs.values();
    const a = it.next().value!;
    const b = it.next().value!;
    return [a, b];
  }

  private startPinch(): void {
    const [a, b] = this.firstTwo();
    this.cam.pinchStart((a.x + b.x) / 2, (a.y + b.y) / 2, Math.hypot(a.x - b.x, a.y - b.y), this.pinch);
  }

  onNav(a: NavAction): boolean {
    // With the card up, Enter / A presses its button and Esc / B puts it away.
    if (this.cardMode && (a === 'confirm' || a === 'back')) {
      if (a === 'confirm') this.confirmCard();
      else this.closeCard();
      return true;
    }
    if (a === 'back' || a === 'confirm') {
      this.host.close();
      return true;
    }
    // Arrow keys / WASD / the D-pad pan the map.
    const step = 120 / this.cam.zoom;
    const c = this.cam;
    if (a === 'left') c.glideTo(c.cx - step, c.cz);
    else if (a === 'right') c.glideTo(c.cx + step, c.cz);
    else if (a === 'up') c.glideTo(c.cx, c.cz - step);
    else if (a === 'down') c.glideTo(c.cx, c.cz + step);
    this.dirty = true;
    return true;
  }

  dispose(): void {
    this.ro?.disconnect();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }
}

/** The scale bar's candidates (m): the first that is at least 60 px wide at the zoom. */
const SCALE_STEPS = [20, 50, 100, 200, 500, 1000];

/** The compass: a ring with N at the top (the map never turns). */
const COMPASS_SVG =
  '<svg viewBox="0 0 40 40" aria-hidden="true"><circle cx="20" cy="20" r="17" fill="none" stroke="currentColor" stroke-opacity="0.45" stroke-width="1.5"/><path d="M20 6l5 14-5-3-5 3z" fill="#e8482f"/><path d="M20 34l-5-14 5 3 5-3z" fill="currentColor" fill-opacity="0.55"/><text x="20" y="4.6" text-anchor="middle" font-size="7" font-family="BH Oswald, Arial Narrow, sans-serif" font-weight="700" fill="currentColor">N</text></svg>';

/** The car silhouette (side on) the key and the Find my car pill use — the same shape the map draws. */
const CAR_SVG =
  '<svg class="car" viewBox="0 0 26 16" aria-hidden="true"><path d="M2.5 11V7.5L6 6.5 9 2.5h8l3.5 4 3 1V11z" fill="currentColor" stroke="#15120f" stroke-width="1.2" stroke-linejoin="round"/><path d="M7.4 6.4 9.8 3.6h6.4l2.8 2.8" fill="none" stroke="rgba(233,224,201,0.6)" stroke-width="1.1"/><circle cx="7" cy="11.4" r="2.6" fill="#15120f"/><circle cx="19" cy="11.4" r="2.6" fill="#15120f"/><circle cx="7" cy="11.4" r="1" fill="#e9e0c9"/><circle cx="19" cy="11.4" r="1" fill="#e9e0c9"/></svg>';
